import { useNavigate } from '@tanstack/react-router';
import { Building2, Globe, List, Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { PositionStatus } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { SkeletonLines } from '../../../components/Skeleton';
import {
  EmptyState,
  ForbiddenState,
  ServerErrorState,
} from '../../../components/system-states/SystemState';
import { ALL_SCOPE, useCanEdit, useCanView, useSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useOrgTree, type OrgTreeNode, type RollupCounts } from '../api/organisation-api';
import { NewUnitDialog } from '../components/NewUnitDialog';
import { OrgTree } from '../components/OrgTree';
import { SeatSummary } from '../components/SeatSummary';
import styles from './OrgTreePage.module.css';

/**
 * ORG-01 — the org tree.
 *
 * The screen's whole purpose is the sentence in the S5 brief: "clicking an org tree
 * node filters the ENTIRE application to that unit." So a click writes the S2 scope
 * store, which the utility bar's indicator and every S5-aware list already read.
 * Nothing here knows what a position ledger is — it sets one value and the rest of
 * the application follows, which is why the same mechanism works for slices that do
 * not exist yet.
 */

/** Picking unit after unit shows one toast about the latest pick, not a stack. */
const SCOPE_TOAST_KEY = 'org-scope';

export function OrgTreePage(): ReactNode {
  const canView = useCanView('org_unit_position');
  const canEdit = useCanEdit('org_unit_position');
  const navigate = useNavigate();
  const scope = useSession((state) => state.scope);
  const setScope = useSession((state) => state.setScope);
  const showToast = useToasts((state) => state.show);
  const [isCreating, setIsCreating] = useState(false);

  const tree = useOrgTree();

  if (!canView) {
    return <ForbiddenState resource="the org structure" contact="Your admin" />;
  }

  if (tree.isError) {
    return <ServerErrorState onRetry={() => void tree.refetch()} />;
  }

  const nodes = tree.data ?? [];
  const selectedId = scope.kind === 'all' ? null : scope.id;
  const totals = sumAll(nodes);

  function openLedger(status?: PositionStatus): void {
    void navigate({
      to: '/organisation/positions',
      search: status ? { status } : {},
    });
  }

  function handleSelect(node: OrgTreeNode): void {
    // Clicking the node you are already filtered to clears the filter. Making one
    // control both set and unset is what stops the tree becoming a place you can
    // get stuck (06-qol §8: back always works, and so should the way out).
    if (node.id === selectedId) {
      setScope(ALL_SCOPE);
      showToast({ tone: 'info', message: 'Showing every venture', key: SCOPE_TOAST_KEY });
      return;
    }

    setScope({ kind: node.kind, id: node.id, label: node.name });
    showToast({
      tone: 'info',
      key: SCOPE_TOAST_KEY,
      message: `Filtered to ${node.name}`,
      detail: 'Every list in the application now shows this unit only.',
      action: { label: 'Show everything', run: () => setScope(ALL_SCOPE) },
    });
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Organisation</h1>
          <p className={styles.lead}>
            Every venture, department and team, with the seats under them. Pick a unit to filter
            the whole application to it.
          </p>
        </div>

        <div className={styles.headActions}>
          <Button variant="ghost" icon={List} onClick={() => openLedger()}>
            Position ledger
          </Button>
          {selectedId !== null && (
            <Button variant="ghost" icon={Globe} onClick={() => setScope(ALL_SCOPE)}>
              Show everything
            </Button>
          )}
          {canEdit && (
            <Button variant="solid" icon={Plus} onClick={() => setIsCreating(true)}>
              Add a unit
            </Button>
          )}
        </div>
      </header>

      {/* 06-qol §12: every number drills through to the records behind it. */}
      <SeatSummary counts={totals} ventureCount={nodes.length} onOpen={openLedger} />

      <Card isFlush>
        {tree.isPending ? (
          // A skeleton only on a first paint — the tree has never been on screen
          // and is not in cache. A refetch keeps the previous tree (§5a rule 2).
          <div className={styles.loading}>
            <SkeletonLines count={8} />
          </div>
        ) : nodes.length === 0 ? (
          <EmptyState
            isInset
            title="No ventures yet"
            body="A venture is the top of the tree. Add one, and its departments, teams and seats hang underneath it."
            action={
              canEdit ? (
                <Button variant="solid" icon={Building2} onClick={() => setIsCreating(true)}>
                  Add a venture
                </Button>
              ) : undefined
            }
          />
        ) : (
          <OrgTree nodes={nodes} selectedId={selectedId} onSelect={handleSelect} />
        )}
      </Card>

      {isCreating && <NewUnitDialog initialKind={nodes.length === 0 ? 'venture' : 'department'} onClose={() => setIsCreating(false)} />}
    </div>
  );
}

/** The organisation-wide totals, which are the ventures added together. */
function sumAll(nodes: readonly OrgTreeNode[]): RollupCounts {
  return nodes.reduce<RollupCounts>(
    (acc, node) => ({
      total: acc.total + node.counts.total,
      filled: acc.filled + node.counts.filled,
      vacant: acc.vacant + node.counts.vacant,
      inRecruitment: acc.inRecruitment + node.counts.inRecruitment,
      planned: acc.planned + node.counts.planned,
      frozen: acc.frozen + node.counts.frozen,
    }),
    { total: 0, filled: 0, vacant: 0, inRecruitment: 0, planned: 0, frozen: 0 },
  );
}
